/**
 * ppd-carousel — the single carousel core shared by the homepage hero collage
 * and the collection carousels.
 *
 * It drives any subtree matching:
 *   [data-ppd-carousel]                 the carousel root
 *     [data-ppd-carousel-slide]         one slide per item (.is-active shows it)
 *     [data-ppd-carousel-viewport]      the swipe/keyboard target
 *     [data-ppd-carousel-toggle]        pause/play button
 *     [data-ppd-carousel-dot]           one tab per slide in the dot tablist;
 *                                       the selected dot also shows autoplay
 *                                       progress via .is-running
 *
 * Behaviour:
 *   - autoplays on `data-ppd-carousel-interval` (ms), default 6000
 *   - does NOT pause when the pointer merely hovers — only the button stops it
 *   - the toggle swaps its pause/play glyph via an `is-paused` class
 *   - ArrowLeft/ArrowRight and horizontal swipe step the slides
 *   - dot tabs reuse show()/step() and keep
 *     touch-action: pan-y on the viewport for vertical scroll
 *   - dot tabs use roving tabindex + aria-selected and announce through the
 *     viewport's existing aria-live polite path (no separate live region)
 *   - honours prefers-reduced-motion (no autoplay)
 *   - a shared IntersectionObserver pauses offscreen carousels (timers only —
 *     the pause/play glyph is untouched) and resumes them on return, unless
 *     the visitor explicitly paused via the toggle
 */
(() => {
  const MOUNT_SELECTOR = '[data-ppd-carousel]';

  /** All mounted carousel roots, in mount order. */
  const mounted = new Set();

  /** Shared observer: one instance for every carousel on the page. */
  let carouselObserver = null;

  function getCarouselObserver() {
    if (carouselObserver) return carouselObserver;
    carouselObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const root = /** @type {Element} */ (entry.target);
        const api = root.__ppdCarousel;
        if (!api) continue;
        if (!root.isConnected) {
          carouselObserver.unobserve(root);
          mounted.delete(root);
          continue;
        }
        if (entry.isIntersecting) api.onVisible();
        else api.onHidden();
      }
    });
    return carouselObserver;
  }

  // One document-level tab switch handler for all carousels (previously each
  // carousel added its own). Semantics are unchanged: hidden stops the timer
  // and shows the paused state; visible restarts it.
  document.addEventListener('visibilitychange', () => {
    for (const root of mounted) {
      const api = root.__ppdCarousel;
      if (!api) continue;
      if (document.hidden) api.onTabHidden();
      else api.onTabVisible();
    }
  });

  function init(root) {
    const slides = Array.from(root.querySelectorAll('[data-ppd-carousel-slide]'));
    if (slides.length < 2) return;

    const viewport = root.querySelector('[data-ppd-carousel-viewport]');
    const toggle = root.querySelector('[data-ppd-carousel-toggle]');
    const dots = Array.from(root.querySelectorAll('[data-ppd-carousel-dot]'));
    const labelPause = root.getAttribute('data-ppd-carousel-label-pause') || 'Pause';
    const labelPlay = root.getAttribute('data-ppd-carousel-label-play') || 'Play';
    const intervalMs = parseInt(
      root.getAttribute('data-ppd-carousel-interval') || '6000',
      10,
    );
    const safeInterval = Number.isFinite(intervalMs) && intervalMs >= 1000 ? intervalMs : 6000;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let index = 0;
    let timer = null;
    let pointerStartX = null;
    // Explicit visitor intent from the toggle. The shared observer never
    // resumes a carousel the visitor paused; tab switches keep the previous
    // resume behaviour.
    let userPaused = false;

    function show(next) {
      slides.forEach((slide, position) => {
        const active = position === next;
        slide.classList.toggle('is-active', active);
        if (active) {
          slide.removeAttribute('aria-hidden');
          slide.removeAttribute('inert');
        } else {
          // Use the explicit ARIA token rather than an empty boolean
          // attribute so assistive technology receives a stable state while
          // the autoplay timer advances between slides.
          slide.setAttribute('aria-hidden', 'true');
          slide.setAttribute('inert', '');
        }
      });
      // The frame follows the active slide's layout pattern (portrait duos
      // widen, landscape duos narrow) so slots match the photos they hold.
      const frame = slides[next].getAttribute('data-frame-ratio');
      const frameNum = slides[next].getAttribute('data-frame-num');
      if (frame) root.style.setProperty('--ppd-carousel-frame', frame);
      if (frameNum) root.style.setProperty('--ppd-carousel-frame-num', frameNum);
      // APG dot tabs: roving tabindex + aria-selected mirror the active slide.
      // Announcement reuses the viewport's aria-live polite path (see
      // setPaused) — no separate live region.
      if (dots.length) {
        dots.forEach((dot, position) => {
          const selected = position === next;
          dot.setAttribute('aria-selected', String(selected));
          dot.setAttribute('tabindex', selected ? '0' : '-1');
        });
      }
    }

    function setPaused(paused) {
      root.classList.toggle('is-paused', paused);
      /* APG carousel: changes are announced only when the visitor is driving
         (paused). While autoplaying, the live region is silenced. */
      if (viewport) viewport.setAttribute('aria-live', paused ? 'polite' : 'off');
      if (toggle) {
        toggle.setAttribute('aria-pressed', String(!paused));
        toggle.setAttribute('aria-label', paused ? labelPlay : labelPause);
        const pauseIcon = toggle.querySelector('.ppd-carousel__icon-pause');
        const playIcon = toggle.querySelector('.ppd-carousel__icon-play');
        if (pauseIcon) pauseIcon.toggleAttribute('hidden', paused);
        if (playIcon) playIcon.toggleAttribute('hidden', !paused);
      }
    }

    function restartProgress() {
      // The selected dot is the progress indicator: move .is-running to it
      // so its fill animation restarts with the new slide's interval.
      dots.forEach((dot) => dot.classList.remove('is-running'));
      const selected = dots[index];
      if (!selected) return;
      void selected.offsetWidth; // reflow so the animation restarts
      if (!reducedMotion) selected.classList.add('is-running');
    }

    function start() {
      if (timer || reducedMotion) return;
      timer = window.setInterval(() => {
        index = (index + 1) % slides.length;
        show(index);
        restartProgress();
      }, safeInterval);
      setPaused(false);
      restartProgress();
    }

    /**
     * Stops the timer. Offscreen pauses pass `markPaused = false` so the
     * toggle glyph and live region keep reflecting visitor intent — only the
     * timer (and its dot progress) rest.
     */
    function stop(markPaused = true) {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
      if (markPaused) setPaused(true);
      dots.forEach((dot) => dot.classList.remove('is-running'));
    }

    function step(direction) {
      index = (index + direction + slides.length) % slides.length;
      show(index);
      if (timer) {
        window.clearInterval(timer);
        timer = null;
        start();
      } else {
        restartProgress();
      }
    }

    // Dot tabs jump to an absolute slide but reuse the same show() + timer
    // restart contract as step() so autoplay, progress, and APG state stay
    // in sync whichever control drives.
    function goTo(target) {
      const normalized = ((target % slides.length) + slides.length) % slides.length;
      index = normalized;
      show(index);
      if (timer) {
        window.clearInterval(timer);
        timer = null;
        start();
      } else {
        restartProgress();
      }
    }

    if (toggle) {
      toggle.addEventListener('click', () => {
        if (timer) {
          userPaused = true;
          stop();
        } else {
          userPaused = false;
          start();
        }
      });
    }

    if (dots.length) {
      dots.forEach((dot, position) => {
        dot.addEventListener('click', () => goTo(position));
        dot.addEventListener('keydown', (event) => {
          let target = null;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            target = (position - 1 + dots.length) % dots.length;
          } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            target = (position + 1) % dots.length;
          } else if (event.key === 'Home') {
            target = 0;
          } else if (event.key === 'End') {
            target = dots.length - 1;
          }
          if (target === null) return;
          event.preventDefault();
          dots[target].focus();
          goTo(target);
        });
      });
    }

    if (viewport) {
      // Native image drag would swallow pointerup and kill mouse swipes.
      viewport.addEventListener('dragstart', (event) => event.preventDefault());

      viewport.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        step(event.key === 'ArrowLeft' ? -1 : 1);
      });

      viewport.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        pointerStartX = event.clientX;
      });
      viewport.addEventListener('pointerup', (event) => {
        if (pointerStartX === null) return;
        const delta = event.clientX - pointerStartX;
        pointerStartX = null;
        if (Math.abs(delta) >= 48) step(delta < 0 ? 1 : -1);
      });
      viewport.addEventListener('pointercancel', () => { pointerStartX = null; });
    }

    // Visibility API shared across carousels (see the document-level handler
    // above) plus the shared IntersectionObserver entry below.
    root.__ppdCarousel = {
      onVisible() {
        if (!userPaused && !reducedMotion && !timer) start();
      },
      onHidden() {
        stop(false);
      },
      onTabHidden() {
        stop();
      },
      onTabVisible() {
        if (!reducedMotion && !timer) start();
      },
    };
    mounted.add(root);
    getCarouselObserver().observe(root);

    show(index);
    if (!reducedMotion) start();
    else setPaused(true);
  }

  function claim(root) {
    if (root.__ppdCarouselInit) return;
    root.__ppdCarouselInit = true;
    init(root);
  }

  document.querySelectorAll(MOUNT_SELECTOR).forEach(claim);

  // Re-run when Shopify injects carousels after a fetch/Morph re-render.
  // Scoped: only added subtrees are inspected for carousel roots, instead of
  // re-scanning the whole document on every mutation. The body-level
  // observation itself stays — carousels can be injected anywhere — until
  // layout owners provide narrower mount containers (see lane-5 notes).
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches(MOUNT_SELECTOR)) claim(node);
        const nested = node.querySelectorAll(MOUNT_SELECTOR);
        for (const root of nested) claim(root);
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();
